/**
 * Crypto Gest — shared display formatters.
 */

/**
 * Canonical ledger entry-type order — drives type filters and any type listing
 * so they read the same everywhere.
 */

export const ENTRY_TYPE_ORDER = [
  'deposit',
  'withdrawal',
  'buy',
  'sell',
  'transfer_in',
  'transfer_out',
  'fee',
  'reward',
  'airdrop'
];

/**
 * Format a USD amount (compact-friendly, 2 decimals).
 */

export function usd(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
}

/**
 * Format a value that is already in `currency` (no conversion). Any ISO 4217
 * code is supported; falls back gracefully if the runtime doesn't know it.
 */

export function formatInCurrency(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  // Sub-cent values (a low-priced token's price/value) would round to "0.00" at
  // 2 decimals — show enough decimals to keep ~2 significant digits instead.
  let maximumFractionDigits = 2;

  if (value !== 0 && Math.abs(value) < 0.01) {
    const leadingZeros = Math.floor(-Math.log10(Math.abs(value)));

    maximumFractionDigits = Math.min(8, leadingZeros + 2);
  }

  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits }).format(value);
  } catch {
    return `${new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(value)} ${currency}`;
  }
}

/**
 * Format a USD value in the chosen display currency. `perUsd` is the number of
 * units of the display currency per 1 USD (1 for USD itself).
 */

export function formatMoney(valueUsd: number | null | undefined, currency: string, perUsd: number): string {
  if (valueUsd === null || valueUsd === undefined || Number.isNaN(valueUsd)) {
    return '—';
  }

  const converted = perUsd > 0 ? valueUsd * perUsd : valueUsd;

  return formatInCurrency(converted, currency);
}

/**
 * Format a profit rate / percentage with a sign.
 */

export function pct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  const sign = value > 0 ? '+' : '';

  return `${sign}${value.toFixed(2)}%`;
}

/**
 * Trim a crypto amount to a readable number of significant digits.
 */

export function amount(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const asNumber = typeof value === 'string' ? parseFloat(value) : value;

  if (Number.isNaN(asNumber)) {
    return String(value);
  }

  const abs = Math.abs(asNumber);

  // Very small dust: show a readable threshold instead of scientific notation.
  if (asNumber !== 0 && abs < 0.00000001) {
    return `${asNumber < 0 ? '-' : ''}<0.00000001`;
  }

  // Always show 8 decimals (crypto precision), padding with zeros — plain
  // decimals rather than scientific notation.
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 8 }).format(asNumber);
}

/**
 * Tailwind text colour for a signed number (green / red / muted).
 */

export function signClass(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) {
    return 'text-muted-foreground';
  }

  return value > 0 ? 'text-[#1B752B]' : 'text-destructive';
}

/**
 * A padded [min, max] domain for a chart Y-axis, so the line sits between the
 * extremes with headroom (e.g. 55k–65k → ~53k–67k) instead of from zero.
 */

export function paddedDomain(values: number[]): [number, number] | undefined {
  const nums = values.filter(value => Number.isFinite(value));

  if (nums.length === 0) {
    return undefined;
  }

  const min = Math.min(...nums);
  const max = Math.max(...nums);

  if (min === max) {
    const pad = Math.abs(min) * 0.05 || 1;

    return [min - pad, max + pad];
  }

  const pad = (max - min) * 0.2;

  return [min - pad, max + pad];
}

/**
 * Short address (0x1234…abcd).
 */

export function shortAddress(address: string | null | undefined): string {
  if (!address) {
    return '';
  }

  return address.length > 14 ? `${address.slice(0, 8)}…${address.slice(-6)}` : address;
}
